import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faPhone, faRupeeSign, faBalanceScale, faShoppingCart,
    faMoneyBill, faHistory, faExclamationTriangle, faFilter, faChevronDown,
    faChevronUp, faChevronLeft, faChevronRight, faSearch, faSpinner, faTimes,
    faSave, faPrint, faReceipt, faUsers, faEye, faCreditCard,
    faWallet, faUniversity, faCheckCircle, faExclamationCircle, faTag, faCalendarAlt,
    faList, faFileInvoice, faCheckSquare, faSquare, faPercent,
    faLandmark, faBarcode, faBuilding, faAddressCard, faIdCard, faCopy,
    faMoneyBillWave, faClock, faUserPlus, faBank, faInfoCircle, faBox,
    faSort, faSortUp, faSortDown, faCalendarWeek, faCalendarDay,
    faExchangeAlt, faArrowDown, faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerAccount.css";
// ==============================================
// Debounce Function
// ==============================================
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// ==============================================
// Toast Notification Component
// ==============================================
const CaToastNotification = ({ toast, setToast, translations }) => {
    if (!toast.show) return null;
    return (
        <div className={`ca-toast-notification ${toast.type}`}>
            <div className="ca-toast-content">
                <div className="ca-toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationCircle} />
                </div>
                <div className="ca-toast-text">
                    <div className="ca-toast-message">{toast.message}</div>
                </div>
                <button className="ca-toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Advanced Filters Component
// ==============================================
const CaAdvancedFilters = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
    showFilters,
    setShowFilters,
    translations,
    transactionTypes = ['sale', 'payment', 'adjustment', 'refund'],
    paymentMethods = ['cash', 'cheque', 'bank_transfer', 'digital_wallet', 'card', 'upi']
}) => {
    const [localFilters, setLocalFilters] = useState(filters);
    const [activePreset, setActivePreset] = useState('all');

    const presets = [
        { id: 'all', label: translations?.all || 'All', days: null },
        { id: 'today', label: translations?.today || 'Today', days: 1 },
        { id: 'yesterday', label: translations?.yesterday || 'Yesterday', days: 1, offset: 1 },
        { id: 'week', label: translations?.thisWeek || 'This Week', days: 7 },
        { id: 'month', label: translations?.thisMonth || 'This Month', days: 30 },
        { id: 'quarter', label: translations?.last3Months || 'Last 3 Months', days: 90 },
        { id: 'year', label: translations?.thisYear || 'This Year', days: 365 }
    ];

    const applyPreset = (preset) => {
        setActivePreset(preset.id);
        const today = new Date();
        let fromDate = null;
        let toDate = null;

        if (preset.days) {
            if (preset.id === 'yesterday') {
                fromDate = new Date(today);
                fromDate.setDate(today.getDate() - 1);
                toDate = new Date(fromDate);
            } else {
                fromDate = new Date(today);
                fromDate.setDate(today.getDate() - preset.days + 1);
                toDate = new Date(today);
            }

            setLocalFilters(prev => ({
                ...prev,
                from_date: fromDate.toISOString().split('T')[0],
                to_date: toDate.toISOString().split('T')[0]
            }));
        } else {
            setLocalFilters(prev => ({
                ...prev,
                from_date: '',
                to_date: ''
            }));
        }
    };

    const handleLocalChange = (field, value) => {
        setLocalFilters(prev => ({ ...prev, [field]: value }));
        setActivePreset('custom');
    };

    const handleApply = () => {
        onFilterChange(localFilters);
        onApplyFilters(localFilters);
    };

    const handleClear = () => {
        const clearedFilters = {
            from_date: '',
            to_date: '',
            transaction_type: '',
            payment_method: '',
            min_amount: '',
            max_amount: '',
            search_text: ''
        };
        setLocalFilters(clearedFilters);
        setActivePreset('all');
        onFilterChange(clearedFilters);
        onClearFilters();
    };

    if (!showFilters) return null;

    return (
        <div className="ca-filters-panel">
            <div className="ca-filters-header">
                <h4><FontAwesomeIcon icon={faFilter} /> {translations?.advancedFilters || 'Advanced Filters'}</h4>
                <button className="ca-filters-close" onClick={() => setShowFilters(false)}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <div className="ca-filters-body">
                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations?.dateRange || 'Date Range'}</label>
                    <div className="ca-date-presets">
                        {presets.map(preset => (
                            <button
                                key={preset.id}
                                className={`ca-preset-btn ${activePreset === preset.id ? 'active' : ''}`}
                                onClick={() => applyPreset(preset)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    <div className="ca-date-range-inputs">
                        <div className="ca-date-input-group">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <input
                                type="date"
                                value={localFilters.from_date || ''}
                                onChange={(e) => handleLocalChange('from_date', e.target.value)}
                                placeholder={translations?.fromDate || 'From Date'}
                            />
                        </div>
                        <span className="ca-date-separator">—</span>
                        <div className="ca-date-input-group">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <input
                                type="date"
                                value={localFilters.to_date || ''}
                                onChange={(e) => handleLocalChange('to_date', e.target.value)}
                                placeholder={translations?.toDate || 'To Date'}
                            />
                        </div>
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations?.transactionType || 'Transaction Type'}</label>
                    <div className="ca-filter-options">
                        <button
                            className={`ca-filter-option ${!localFilters.transaction_type ? 'active' : ''}`}
                            onClick={() => handleLocalChange('transaction_type', '')}
                        >
                            {translations?.all || 'All'}
                        </button>
                        {transactionTypes.map(type => (
                            <button
                                key={type}
                                className={`ca-filter-option ${localFilters.transaction_type === type ? 'active' : ''}`}
                                onClick={() => handleLocalChange('transaction_type', type)}
                            >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations?.paymentMethod || 'Payment Method'}</label>
                    <div className="ca-filter-options">
                        <button
                            className={`ca-filter-option ${!localFilters.payment_method ? 'active' : ''}`}
                            onClick={() => handleLocalChange('payment_method', '')}
                        >
                            {translations?.all || 'All'}
                        </button>
                        {paymentMethods.map(method => (
                            <button
                                key={method}
                                className={`ca-filter-option ${localFilters.payment_method === method ? 'active' : ''}`}
                                onClick={() => handleLocalChange('payment_method', method)}
                            >
                                {method.replace('_', ' ').charAt(0).toUpperCase() + method.replace('_', ' ').slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations?.amountRange || 'Amount Range'}</label>
                    <div className="ca-amount-range-inputs">
                        <div className="ca-amount-input-group">
                            <FontAwesomeIcon icon={faRupeeSign} />
                            <input
                                type="number"
                                placeholder={translations?.minAmount || 'Min Amount'}
                                value={localFilters.min_amount || ''}
                                onChange={(e) => handleLocalChange('min_amount', e.target.value)}
                                step="0.01"
                            />
                        </div>
                        <span className="ca-amount-separator">—</span>
                        <div className="ca-amount-input-group">
                            <FontAwesomeIcon icon={faRupeeSign} />
                            <input
                                type="number"
                                placeholder={translations?.maxAmount || 'Max Amount'}
                                value={localFilters.max_amount || ''}
                                onChange={(e) => handleLocalChange('max_amount', e.target.value)}
                                step="0.01"
                            />
                        </div>
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations?.search || 'Search'}</label>
                    <div className="ca-search-input-group">
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                            type="text"
                            placeholder={translations?.searchByDescription || 'Search by description, reference, notes...'}
                            value={localFilters.search_text || ''}
                            onChange={(e) => handleLocalChange('search_text', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="ca-filters-footer">
                <button className="ca-btn-clear-filters" onClick={handleClear}>
                    <FontAwesomeIcon icon={faTimes} /> {translations?.clearFilters || 'Clear Filters'}
                </button>
                <button className="ca-btn-apply-filters" onClick={handleApply}>
                    <FontAwesomeIcon icon={faCheckCircle} /> {translations?.applyFilters || 'Apply Filters'}
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Sortable Table Header Component
// ==============================================
const CaSortableHeader = ({ column, label, sortConfig, onSort, translations }) => {
    const isActive = sortConfig.key === column;
    return (
        <th onClick={() => onSort(column)} className="ca-sortable-header">
            {label}
            <span className="ca-sort-indicator">
                {isActive ? (
                    sortConfig.direction === 'asc' ?
                        <FontAwesomeIcon icon={faSortUp} /> :
                        <FontAwesomeIcon icon={faSortDown} />
                ) : (
                    <FontAwesomeIcon icon={faSort} />
                )}
            </span>
        </th>
    );
};

// ==============================================
// Customer Search Component
// ==============================================
const CaCustomerSearch = ({ onSelectCustomer, selectedCustomer, shopId, translations, isRTL, showToast }) => {
    const [searchTerm, setSearchTerm] = useState(selectedCustomer?.customer_name || '');
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);

    const loadAllCustomers = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_all_customers_account',
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setCustomers(data.data || []);
                setFilteredCustomers(data.data || []);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        } finally {
            setLoading(false);
        }
    }, [shopId]);

    const performSearch = useCallback(async (term) => {
        if (!shopId) return;

        if (!term || term.trim() === '') {
            setFilteredCustomers(customers);
            setShowDropdown(true);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_customers_account',
                    search_term: term,
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFilteredCustomers(data.data || []);
                setShowDropdown(data.data && data.data.length > 0);
            } else {
                setFilteredCustomers([]);
                setShowDropdown(false);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setFilteredCustomers([]);
            setShowDropdown(false);
        } finally {
            setLoading(false);
        }
    }, [shopId, customers]);

    const debouncedSearch = useCallback(
        debounce((term) => {
            performSearch(term);
        }, 300),
        [performSearch]
    );

    useEffect(() => {
        loadAllCustomers();
    }, [loadAllCustomers]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.length >= 1) {
            debouncedSearch(value);
        } else {
            setFilteredCustomers(customers);
            setShowDropdown(true);
        }
    };

    const handleSelect = (customer) => {
        setSearchTerm(customer.customer_name);
        setShowDropdown(false);
        if (onSelectCustomer) {
            onSelectCustomer(customer);
        }
        if (showToast) {
            showToast('success', `Customer selected: ${customer.customer_name}`);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    return (
        <div className="ca-customer-search-group" ref={searchRef}>
            <div className="ca-input-container">
                <FontAwesomeIcon icon={faSearch} className="ca-input-icon ca-search-icon" />
                <input
                    type="text"
                    id="ca_customer_search"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => {
                        if (searchTerm && searchTerm.length >= 1) {
                            debouncedSearch(searchTerm);
                        } else {
                            setFilteredCustomers(customers);
                            setShowDropdown(true);
                        }
                    }}
                    placeholder={translations?.selectCustomer || 'Search customer by name or phone...'}
                    disabled={loading}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    autoComplete="off"
                    className="ca-customer-search-input"
                />
                <label htmlFor="ca_customer_search" className="ca-input-label">
                    {translations?.selectCustomer || 'Select Customer'} <span className="ca-required">*</span>
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ca-dropdown-icon" />
                {loading && (
                    <div className="ca-search-loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                )}
            </div>

            {showDropdown && (
                <div className="ca-customer-search-dropdown">
                    {filteredCustomers.length > 0 ? (
                        filteredCustomers.map(customer => (
                            <div
                                key={customer.customer_id}
                                className="ca-customer-option"
                                onClick={() => handleSelect(customer)}
                            >
                                <div className="ca-customer-name-display">
                                    <FontAwesomeIcon icon={faUser} />
                                    {customer.customer_name}
                                </div>
                                <div className="ca-customer-info-display">
                                    {customer.phone && (
                                        <span>
                                            <FontAwesomeIcon icon={faPhone} />
                                            {customer.phone}
                                        </span>
                                    )}
                                    <span className="ca-customer-balance-display">
                                        Balance: {formatCurrency(customer.current_balance)}
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="ca-customer-dropdown-empty">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{translations?.noCustomersFound || 'No customers found'}</span>
                        </div>
                    )}
                </div>
            )}

            {selectedCustomer && (
                <div className="ca-selected-customer-info">
                    <div className="ca-info-row">
                        <FontAwesomeIcon icon={faUser} />
                        <span>{selectedCustomer.customer_name}</span>
                    </div>
                    {selectedCustomer.phone && (
                        <div className="ca-info-row">
                            <FontAwesomeIcon icon={faPhone} />
                            <span>{selectedCustomer.phone}</span>
                        </div>
                    )}
                    <div className="ca-info-row ca-balance">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                        <span>Balance: {formatCurrency(selectedCustomer.current_balance)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Cheque Details Form Component
// ==============================================
const CaChequeDetailsForm = ({ chequeDetails, onChequeChange, errors, translations, isRTL }) => {
    const currencyOptions = [
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },
        { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
        { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
        { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' }
    ];

    const convertNumberToWords = (num) => {
        if (!num || isNaN(num)) return '';
        const number = parseFloat(num);
        const whole = Math.floor(number);
        const cents = Math.round((number - whole) * 100);
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
            'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const numToWords = (n) => {
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + numToWords(n % 100) : '');
            if (n < 1000000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
            if (n < 1000000000) return numToWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + numToWords(n % 1000000) : '');
            return numToWords(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + numToWords(n % 1000000000) : '');
        };
        let words = numToWords(whole);
        if (cents > 0) words += ` and ${cents}/100`;
        return words;
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
            onChequeChange('amount', value);
            if (value && !isNaN(value) && parseFloat(value) > 0) {
                onChequeChange('amount_in_words', convertNumberToWords(value));
            } else {
                onChequeChange('amount_in_words', '');
            }
        }
    };

    const handleCurrencyChange = (e) => {
        const code = e.target.value;
        const currency = currencyOptions.find(c => c.code === code);
        onChequeChange('currency_code', code);
        onChequeChange('currency_symbol', currency ? currency.symbol : '$');
    };

    return (
        <div className="ca-cheque-details-form">
            <div className="ca-form-section">
                <h4 className="ca-section-subtitle"><FontAwesomeIcon icon={faUniversity} /> {translations?.chequeDetails || 'Cheque Details'}</h4>
                <div className="ca-form-grid">
                    <div className={`ca-form-group ${errors.cheque_no ? 'has-error' : ''}`}>
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.cheque_no || ''} onChange={(e) => onChequeChange('cheque_no', e.target.value)} placeholder=" " dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="ca-input-label">{translations?.chequeNo || 'Cheque Number'} <span className="ca-required">*</span></label>
                        </div>
                        {errors.cheque_no && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.cheque_no}</div>}
                    </div>
                    <div className={`ca-form-group ${errors.bank_name ? 'has-error' : ''}`}>
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faLandmark} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.bank_name || ''} onChange={(e) => onChequeChange('bank_name', e.target.value)} placeholder=" " dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="ca-input-label">{translations?.bankName || 'Bank Name'} <span className="ca-required">*</span></label>
                        </div>
                        {errors.bank_name && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.bank_name}</div>}
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.amount || ''} onChange={handleAmountChange} placeholder=" " dir="ltr" />
                            <label className="ca-input-label">{translations?.amount || 'Amount'} <span className="ca-required">*</span></label>
                        </div>
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faMoneyBill} className="ca-input-icon" />
                            <select value={chequeDetails.currency_code || 'USD'} onChange={handleCurrencyChange} dir={isRTL ? 'rtl' : 'ltr'}>
                                {currencyOptions.map(option => (<option key={option.code} value={option.code}>{option.code} ({option.symbol})</option>))}
                            </select>
                            <label className="ca-input-label">{translations?.currency || 'Currency'}</label>
                        </div>
                    </div>
                    <div className={`ca-form-group ${errors.cheque_date ? 'has-error' : ''}`}>
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                            <input type="date" value={chequeDetails.cheque_date || ''} onChange={(e) => onChequeChange('cheque_date', e.target.value)} placeholder=" " dir="ltr" />
                            <label className="ca-input-label">{translations?.chequeDate || 'Cheque Date'} <span className="ca-required">*</span></label>
                        </div>
                        {errors.cheque_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.cheque_date}</div>}
                    </div>
                    <div className={`ca-form-group ${errors.issue_date ? 'has-error' : ''}`}>
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                            <input type="date" value={chequeDetails.issue_date || new Date().toISOString().split('T')[0]} onChange={(e) => onChequeChange('issue_date', e.target.value)} placeholder=" " dir="ltr" />
                            <label className="ca-input-label">{translations?.issueDate || 'Issue Date'} <span className="ca-required">*</span></label>
                        </div>
                        {errors.issue_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.issue_date}</div>}
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faAddressCard} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.account_number || ''} onChange={(e) => onChequeChange('account_number', e.target.value)} placeholder=" " dir="ltr" />
                            <label className="ca-input-label">{translations?.accountNumber || 'Account Number'}</label>
                        </div>
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faIdCard} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.drawer_name || ''} onChange={(e) => onChequeChange('drawer_name', e.target.value)} placeholder=" " dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="ca-input-label">{translations?.drawerName || 'Drawer Name'}</label>
                        </div>
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faPhone} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.drawer_phone || ''} onChange={(e) => onChequeChange('drawer_phone', e.target.value)} placeholder=" " dir="ltr" />
                            <label className="ca-input-label">{translations?.drawerPhone || 'Drawer Phone'}</label>
                        </div>
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faBuilding} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.bank_branch || ''} onChange={(e) => onChequeChange('bank_branch', e.target.value)} placeholder=" " dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="ca-input-label">{translations?.bankBranch || 'Bank Branch'}</label>
                        </div>
                    </div>
                    <div className="ca-form-group">
                        <div className="ca-input-container">
                            <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                            <input type="text" value={chequeDetails.bank_code || ''} onChange={(e) => onChequeChange('bank_code', e.target.value)} placeholder=" " dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="ca-input-label">{translations?.bankCode || 'Bank Code'}</label>
                        </div>
                    </div>
                </div>
                <div className="ca-form-group ca-full-width">
                    <div className="ca-input-container">
                        <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                        <input type="text" value={chequeDetails.amount_in_words || ''} onChange={(e) => onChequeChange('amount_in_words', e.target.value)} placeholder=" " dir={isRTL ? 'rtl' : 'ltr'} />
                        <label className="ca-input-label">{translations?.amountInWords || 'Amount in Words'}</label>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Customer to Customer Transfer Modal Component
// ==============================================
const CaCustomerTransferModal = ({ show, fromCustomer, onClose, onSave, translations, showToast, currentShopId, onRefresh }) => {
    const [toCustomer, setToCustomer] = useState(null);
    const [amount, setAmount] = useState('');
    const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const searchRef = useRef(null);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show) {
            setToCustomer(null);
            setAmount('');
            setTransferDate(new Date().toISOString().split('T')[0]);
            setReferenceNo('');
            setNotes('');
            setSearchTerm('');
            setSearchResults([]);
            setErrors({});
            setShowDropdown(false);
        }
    }, [show]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchCustomers = useCallback(async (term) => {
        if (!shopId || !fromCustomer || !term || term.trim().length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        setSearching(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_customers_for_transfer',
                    search_term: term,
                    shop_id: shopId,
                    exclude_customer_id: fromCustomer.customer_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSearchResults(data.data || []);
                setShowDropdown(data.data && data.data.length > 0);
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setSearchResults([]);
            setShowDropdown(false);
        } finally {
            setSearching(false);
        }
    }, [shopId, fromCustomer]);

    const debouncedSearch = useCallback(
        debounce((term) => {
            searchCustomers(term);
        }, 300),
        [searchCustomers]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.length >= 2) {
            debouncedSearch(value);
        } else {
            setSearchResults([]);
            setShowDropdown(false);
        }
    };

    const handleSelectCustomer = (customer) => {
        setToCustomer(customer);
        setSearchTerm(customer.customer_name);
        setShowDropdown(false);
        if (showToast) {
            showToast('success', `Customer selected: ${customer.customer_name}`);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!toCustomer) {
            newErrors.to_customer = translations?.selectDestinationCustomer || 'Please select a destination customer';
        }

        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = translations?.validAmountRequired || 'Please enter a valid amount';
        } else if (parseFloat(amount) > (fromCustomer?.current_balance || 0)) {
            newErrors.amount = translations?.insufficientBalance || 'Amount exceeds available balance';
        }

        if (!transferDate) {
            newErrors.transfer_date = translations?.transferDateRequired || 'Transfer date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const payload = {
                action: 'transfer_customer_balance',
                from_customer_id: fromCustomer.customer_id,
                to_customer_id: toCustomer.customer_id,
                shop_id: shopId,
                amount: parseFloat(amount),
                transfer_date: transferDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                idempotency_key: transactionId
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                if (showToast) {
                    showToast('success', data.message || translations?.transferSuccess || 'Balance transferred successfully');
                }
                if (onSave) {
                    await onSave(data.data);
                }
                if (onRefresh) {
                    onRefresh();
                }
                onClose();
            } else {
                if (showToast) {
                    showToast('error', data.message || translations?.transferFailed || 'Transfer failed');
                } else {
                    alert(data.message || translations?.transferFailed || 'Transfer failed');
                }
            }
        } catch (error) {
            console.error('Transfer error:', error);
            if (showToast) {
                showToast('error', translations?.networkError || 'Network error occurred');
            } else {
                alert(translations?.networkError || 'Network error occurred');
            }
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-transfer-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faExchangeAlt} /> {translations?.customerToCustomerTransfer || 'Customer to Customer Transfer'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    {fromCustomer && (
                        <div className="ca-source-customer-info">
                            <div className="ca-info-label">
                                <FontAwesomeIcon icon={faUser} /> {translations?.sourceCustomer || 'Source Customer'}:
                            </div>
                            <div className="ca-info-value">
                                <strong>{fromCustomer.customer_name}</strong>
                                <span className="ca-balance-info">
                                    {translations?.availableBalance || 'Available Balance'}:
                                    <span className={parseFloat(fromCustomer.current_balance) > 0 ? 'positive' : ''}>
                                        {formatCurrency(fromCustomer.current_balance || 0)}
                                    </span>
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="ca-transfer-arrow">
                        <FontAwesomeIcon icon={faArrowDown} />
                    </div>

                    <div className="ca-destination-customer-section">
                        <div className="ca-form-group" ref={searchRef}>
                            <div className="ca-input-container">
                                <FontAwesomeIcon icon={faSearch} className="ca-input-icon ca-search-icon" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onFocus={() => {
                                        if (searchTerm && searchTerm.length >= 2) {
                                            searchCustomers(searchTerm);
                                        }
                                    }}
                                    placeholder={translations?.searchDestinationCustomer || 'Search destination customer by name or phone...'}
                                    disabled={searching}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    autoComplete="off"
                                />
                                <label className="ca-input-label">
                                    {translations?.destinationCustomer || 'Destination Customer'} <span className="ca-required">*</span>
                                </label>
                                {searching && (
                                    <div className="ca-search-loading">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    </div>
                                )}
                            </div>

                            {showDropdown && searchResults.length > 0 && (
                                <div className="ca-customer-search-dropdown">
                                    {searchResults.map(customer => (
                                        <div
                                            key={customer.customer_id}
                                            className="ca-customer-option"
                                            onClick={() => handleSelectCustomer(customer)}
                                        >
                                            <div className="ca-customer-name-display">
                                                <FontAwesomeIcon icon={faUser} />
                                                {customer.customer_name}
                                            </div>
                                            {customer.phone && (
                                                <div className="ca-customer-phone-display">
                                                    <FontAwesomeIcon icon={faPhone} />
                                                    {customer.phone}
                                                </div>
                                            )}
                                            <div className="ca-customer-balance-display">
                                                {translations?.currentBalance || 'Balance'}: {formatCurrency(customer.current_balance)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {errors.to_customer && (
                                <div className="ca-error-message">
                                    <FontAwesomeIcon icon={faExclamationTriangle} /> {errors.to_customer}
                                </div>
                            )}
                        </div>

                        {toCustomer && (
                            <div className="ca-selected-destination-info">
                                <div className="ca-info-row">
                                    <FontAwesomeIcon icon={faUser} />
                                    <span><strong>{toCustomer.customer_name}</strong></span>
                                </div>
                                {toCustomer.phone && (
                                    <div className="ca-info-row">
                                        <FontAwesomeIcon icon={faPhone} />
                                        <span>{toCustomer.phone}</span>
                                    </div>
                                )}
                                <div className="ca-info-row">
                                    {translations?.currentBalance || 'Balance'}: {formatCurrency(toCustomer.current_balance)}
                                </div>
                            </div>
                        )}
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faMoneyBillWave} /> {translations?.transferDetails || 'Transfer Details'}</h3>

                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.transferAmount || 'Transfer Amount'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                </div>

                                <div className={`ca-form-group ${errors.transfer_date ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input
                                            type="date"
                                            value={transferDate}
                                            onChange={(e) => setTransferDate(e.target.value)}
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.transferDate || 'Transfer Date'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.transfer_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.transfer_date}</div>}
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input
                                            type="text"
                                            value={referenceNo}
                                            onChange={(e) => setReferenceNo(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="ca-input-label">{translations?.referenceNumber || 'Reference Number'}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">{translations?.notes || 'Notes'}</label>
                                </div>
                            </div>
                        </div>

                        {amount && parseFloat(amount) > 0 && fromCustomer && (
                            <div className="ca-transfer-summary">
                                <h4>{translations?.transferSummary || 'Transfer Summary'}</h4>
                                <div className="ca-summary-row">
                                    <span>{translations?.sourceCustomer || 'Source Customer'}:</span>
                                    <span><strong>{fromCustomer.customer_name}</strong></span>
                                </div>
                                <div className="ca-summary-row">
                                    <span>{translations?.destinationCustomer || 'Destination Customer'}:</span>
                                    <span><strong>{toCustomer?.customer_name || '—'}</strong></span>
                                </div>
                                <div className="ca-summary-row">
                                    <span>{translations?.transferAmount || 'Transfer Amount'}:</span>
                                    <span><strong className="ca-positive">{formatCurrency(amount)}</strong></span>
                                </div>
                                <div className="ca-summary-row ca-border-top">
                                    <span>{translations?.sourceBalanceAfter || 'Source Balance After'}:</span>
                                    <span>{formatCurrency((fromCustomer.current_balance || 0) - parseFloat(amount))}</span>
                                </div>
                                {toCustomer && (
                                    <div className="ca-summary-row">
                                        <span>{translations?.destinationBalanceAfter || 'Destination Balance After'}:</span>
                                        <span>{formatCurrency((toCustomer.current_balance || 0) + parseFloat(amount))}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                {translations?.cancel || 'Cancel'}
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> {translations?.processing || 'Processing...'}</>
                                ) : (
                                    <><FontAwesomeIcon icon={faExchangeAlt} /> {translations?.transferBalance || 'Transfer Balance'}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Manual Balance Addition Modal Component
// ==============================================
const CaManualBalanceModal = ({ show, customer, onClose, onSave, translations, showToast, currentShopId, onRefresh }) => {
    const [amount, setAmount] = useState('');
    const [balanceDate, setBalanceDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && customer) {
            setAmount('');
            setBalanceDate(new Date().toISOString().split('T')[0]);
            setReferenceNo('');
            setDescription('');
            setNotes('');
            setErrors({});
        }
    }, [show, customer]);

    const validateForm = () => {
        const newErrors = {};

        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = translations?.validAmountRequired || 'Please enter a valid amount';
        }

        if (!balanceDate) {
            newErrors.balance_date = translations?.balanceDateRequired || 'Balance date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const payload = {
                action: 'add_manual_balance',
                customer_id: customer.customer_id,
                shop_id: shopId,
                amount: parseFloat(amount),
                balance_date: balanceDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                description: description || 'Manual balance added for previous dues',
                idempotency_key: transactionId
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                if (showToast) {
                    showToast('success', data.message || translations?.manualBalanceSuccess || 'Manual balance added successfully');
                }
                if (onSave) {
                    await onSave(data.data);
                }
                if (onRefresh) {
                    onRefresh();
                }
                onClose();
            } else {
                if (showToast) {
                    showToast('error', data.message || translations?.manualBalanceFailed || 'Failed to add manual balance');
                } else {
                    alert(data.message || translations?.manualBalanceFailed || 'Failed to add manual balance');
                }
            }
        } catch (error) {
            console.error('Manual balance error:', error);
            if (showToast) {
                showToast('error', translations?.networkError || 'Network error occurred');
            } else {
                alert(translations?.networkError || 'Network error occurred');
            }
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-manual-balance-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faPlusCircle} /> {translations?.manualBalance || 'Add Manual Balance'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    {customer && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">
                                <FontAwesomeIcon icon={faUser} />
                                {customer.customer_name}
                            </div>
                            {customer.phone && (
                                <div className="ca-customer-phone-large">
                                    <FontAwesomeIcon icon={faPhone} />
                                    {customer.phone}
                                </div>
                            )}
                            <div className="ca-current-balance">
                                <span>{translations?.currentBalance || 'Current Balance'}: </span>
                                <span>{formatCurrency(customer.current_balance || 0)}</span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faFileInvoice} /> {translations?.balanceDetails || 'Balance Details'}</h3>

                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.amount || 'Amount'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                    <div className="ca-help-text">
                                        {translations?.manualBalanceHelp || 'Enter the previous due amount to add to customer\'s account'}
                                    </div>
                                </div>

                                <div className={`ca-form-group ${errors.balance_date ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input
                                            type="date"
                                            value={balanceDate}
                                            onChange={(e) => setBalanceDate(e.target.value)}
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.balanceDate || 'Balance Date'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.balance_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.balance_date}</div>}
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input
                                            type="text"
                                            value={referenceNo}
                                            onChange={(e) => setReferenceNo(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="ca-input-label">{translations?.referenceNumber || 'Reference Number'}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faFileInvoice} className="ca-input-icon" />
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label className="ca-input-label">{translations?.description || 'Description'}</label>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">{translations?.notes || 'Notes (e.g., Previous purchases, Old dues, etc.)'}</label>
                                </div>
                            </div>
                        </div>

                        {amount && parseFloat(amount) > 0 && customer && (
                            <div className="ca-balance-summary">
                                <h4>{translations?.balanceSummary || 'Balance Summary'}</h4>
                                <div className="ca-summary-row">
                                    <span>{translations?.currentBalance || 'Current Balance'}:</span>
                                    <span>{formatCurrency(customer.current_balance || 0)}</span>
                                </div>
                                <div className="ca-summary-row ca-success">
                                    <span>{translations?.amountToAdd || 'Amount to Add'}:</span>
                                    <span>+ {formatCurrency(amount)}</span>
                                </div>
                                <div className="ca-summary-row ca-border-top ca-bold">
                                    <span>{translations?.newBalance || 'New Balance After Addition'}:</span>
                                    <span>{formatCurrency((customer.current_balance || 0) + parseFloat(amount))}</span>
                                </div>
                            </div>
                        )}

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                {translations?.cancel || 'Cancel'}
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> {translations?.adding || 'Adding...'}</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> {translations?.addBalance || 'Add Balance'}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Enhanced Payment Modal Component
// ==============================================
const CaEnhancedPaymentModal = ({ show, customer, onClose, onSave, translations, showToast, currentShopId }) => {
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [chequeDetails, setChequeDetails] = useState({
        cheque_no: '', bank_name: '', amount: '', amount_in_words: '',
        currency_code: 'USD', currency_symbol: '$', cheque_date: '',
        issue_date: new Date().toISOString().split('T')[0], account_number: '',
        drawer_name: '', drawer_phone: '', bank_branch: '', bank_code: ''
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && customer) {
            setAmount('');
            setReferenceNo('');
            setNotes('');
            setSelectedPartner(null);
            setPaymentMethod('cash');
            setChequeDetails({
                cheque_no: '', bank_name: '', amount: '', amount_in_words: '',
                currency_code: 'USD', currency_symbol: '$', cheque_date: '',
                issue_date: new Date().toISOString().split('T')[0], account_number: '',
                drawer_name: '', drawer_phone: '', bank_branch: '', bank_code: ''
            });
            setErrors({});
        }
    }, [show, customer]);

    const validateForm = () => {
        const newErrors = {};
        if (!amount || parseFloat(amount) <= 0) newErrors.amount = translations?.invalidAmount || 'Please enter a valid amount';
        else if (parseFloat(amount) > (customer?.current_balance || 0) && (customer?.current_balance || 0) > 0)
            newErrors.amount = translations?.amountExceedsBalance || 'Payment amount exceeds current balance';

        if (paymentMethod === 'cheque') {
            if (!chequeDetails.cheque_no) newErrors.cheque_no = translations?.chequeNoRequired || 'Cheque number is required';
            if (!chequeDetails.bank_name) newErrors.bank_name = translations?.bankNameRequired || 'Bank name is required';
            if (!chequeDetails.cheque_date) newErrors.cheque_date = translations?.chequeDateRequired || 'Cheque date is required';
            if (!chequeDetails.issue_date) newErrors.issue_date = translations?.issueDateRequired || 'Issue date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            let referenceType = 'payment';
            if (paymentMethod === 'cash') referenceType = 'cash_payment';
            else if (paymentMethod === 'cheque') referenceType = 'cheque_payment';
            else if (paymentMethod === 'bank_transfer') referenceType = 'bank_transfer_payment';
            else if (paymentMethod === 'digital_wallet') referenceType = 'digital_wallet_payment';

            const payload = {
                action: 'record_payment',
                customer_id: customer.customer_id,
                shop_id: shopId,
                amount: parseFloat(amount),
                payment_method: paymentMethod,
                payment_date: paymentDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                idempotency_key: transactionId,
                reference_type: referenceType
            };

            if (paymentMethod === 'cheque') {
                payload.cheque_details = chequeDetails;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                await onSave({ ...data.data, payment_method: paymentMethod });
                onClose();
            } else {
                if (showToast) showToast('error', data.message || translations?.paymentFailed || 'Payment failed');
                else alert(data.message || translations?.paymentFailed || 'Payment failed');
            }
        } catch (error) {
            console.error('Payment error:', error);
            if (showToast) showToast('error', translations?.networkError || 'Network error occurred');
            else alert(translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';
    const paymentMethods = [
        { value: 'cash', label: translations?.cash || 'Cash', icon: faMoneyBill },
        { value: 'cheque', label: translations?.cheque || 'Cheque', icon: faUniversity },
        { value: 'bank_transfer', label: translations?.bankTransfer || 'Bank Transfer', icon: faLandmark },
        { value: 'digital_wallet', label: translations?.digitalWallet || 'Digital Wallet', icon: faWallet }
    ];

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-payment-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faMoneyBillWave} /> {translations?.receivePayment || 'Receive Payment'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    {customer && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">{customer.customer_name}</div>
                            {customer.phone && <div className="ca-customer-phone-large"><FontAwesomeIcon icon={faPhone} /> {customer.phone}</div>}
                            <div className="ca-current-balance">
                                <span>{translations?.currentBalance || 'Balance Due'}:</span>
                                <span className={parseFloat(customer.current_balance) > 0 ? 'positive' : ''}>
                                    Rs. {formatCurrency(customer.current_balance || 0)}
                                </span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faCreditCard} /> {translations?.paymentMethod || 'Payment Method'}</h3>
                            <div className="ca-payment-methods-grid">
                                {paymentMethods.map(method => (
                                    <button key={method.value} type="button" className={`ca-payment-method-btn ${paymentMethod === method.value ? 'active' : ''}`} onClick={() => setPaymentMethod(method.value)} disabled={saving}>
                                        <FontAwesomeIcon icon={method.icon} /><span>{method.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faFileInvoice} /> {translations?.paymentDetails || 'Payment Details'}</h3>
                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="ca-input-icon" />
                                        <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder=" " disabled={saving} dir="ltr" />
                                        <label className="ca-input-label">{translations?.amount || 'Amount'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                </div>
                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} disabled={saving} dir="ltr" />
                                        <label className="ca-input-label">{translations?.paymentDate || 'Payment Date'}</label>
                                    </div>
                                </div>
                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input type="text" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder=" " disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                                        <label className="ca-input-label">{translations?.referenceNo || 'Reference No'}</label>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder=" " disabled={saving} rows="3" dir={isRTL ? 'rtl' : 'ltr'}></textarea>
                                    <label className="ca-input-label">{translations?.notes || 'Notes'}</label>
                                </div>
                            </div>
                        </div>

                        {paymentMethod === 'cheque' && (
                            <CaChequeDetailsForm chequeDetails={chequeDetails} onChequeChange={(field, value) => setChequeDetails(prev => ({ ...prev, [field]: value }))} errors={errors} translations={translations} isRTL={isRTL} />
                        )}

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>{translations?.cancel || 'Cancel'}</button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> {translations?.saving || 'Saving...'}</> : <><FontAwesomeIcon icon={faSave} /> {translations?.savePayment || 'Save Payment'}</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Payment Receipt Modal
// ==============================================
const CaPaymentReceiptModal = ({ show, payment, onClose, translations }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN');
    };
    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    if (!show || !payment) return null;
    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-receipt-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faReceipt} /> {translations?.paymentReceipt || 'Payment Receipt'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    <div className="ca-receipt-preview">
                        <div className="ca-text-center"><strong>{translations?.paymentReceipt || 'PAYMENT RECEIPT'}</strong></div>
                        <hr />
                        <div><strong>{translations?.receiptNo || 'Receipt No'}:</strong> #{payment.receipt_no}</div>
                        <div><strong>{translations?.date || 'Date'}:</strong> {formatDate(payment.payment_date)}</div>
                        <div><strong>{translations?.customerName || 'Customer'}:</strong> {payment.customer_name}</div>
                        <div><strong>{translations?.amount || 'Amount'}:</strong> Rs. {formatCurrency(payment.amount)}</div>
                        <div><strong>{translations?.paymentMethod || 'Method'}:</strong> {payment.payment_method}</div>
                        {payment.reference_no && <div><strong>{translations?.referenceNumber || 'Reference'}:</strong> {payment.reference_no}</div>}
                        {payment.cheque_no && <div><strong>{translations?.chequeNo || 'Cheque No'}:</strong> {payment.cheque_no}</div>}
                        {payment.partner_name && <div><strong>{translations?.partner || 'Partner'}:</strong> {payment.partner_name}</div>}
                        <div><strong>{translations?.balanceAfter || 'Balance After'}:</strong> Rs. {formatCurrency(payment.balance_after)}</div>
                        <hr />
                        <div className="ca-text-center">{translations?.thankYou || 'Thank you for your payment!'}</div>
                    </div>
                </div>
                <div className="ca-modal-footer">
                    <button className="ca-btn-secondary" onClick={onClose}>{translations?.close || 'Close'}</button>
                    <button className="ca-btn-primary" onClick={() => window.print()}><FontAwesomeIcon icon={faPrint} /> {translations?.print || 'Print'}</button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Main Customer Account Component
// ==============================================
const CustomerAccount = ({ embedded = false }) => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [allCustomers, setAllCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [accountData, setAccountData] = useState({
        current_balance: 0,
        transactions: [],
        payments: [],
        unpaid_sales: [],
        transfers: [],
        manual_balances: []
    });
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('transactions');
    const [showSimplePaymentModal, setShowSimplePaymentModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showManualBalanceModal, setShowManualBalanceModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [translations, setTranslations] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');

    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        from_date: '',
        to_date: '',
        transaction_type: '',
        payment_method: '',
        min_amount: '',
        max_amount: '',
        search_text: ''
    });
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [filteredTransfers, setFilteredTransfers] = useState([]);
    const [filteredManualBalances, setFilteredManualBalances] = useState([]);

    const [sortConfig, setSortConfig] = useState({
        key: 'transaction_date',
        direction: 'desc'
    });

    useEffect(() => {
        console.log('CustomerAccount - shopId:', shopId);
        console.log('CustomerAccount - currentShop:', currentShop);
        console.log('CustomerAccount - user:', user);
    }, [shopId, currentShop, user]);

    useEffect(() => {
        const defaultTexts = {
            customerAccount: 'Customer Account',
            accountDetails: 'View and manage customer accounts',
            currentBalance: 'Balance Due',
            totalPurchases: 'Total Purchases',
            totalPayments: 'Total Payments',
            unpaidAmount: 'Unpaid Amount',
            transactionHistory: 'Transactions',
            payments: 'Payments',
            transfers: 'Transfers',
            manualBalance: 'Add Manual Balance',
            manualBalanceSuccess: 'Manual balance added successfully',
            manualBalanceFailed: 'Failed to add manual balance',
            balanceDetails: 'Balance Details',
            balanceDate: 'Balance Date',
            balanceDateRequired: 'Balance date is required',
            balanceSummary: 'Balance Summary',
            amountToAdd: 'Amount to Add',
            newBalance: 'New Balance After Addition',
            adding: 'Adding...',
            addBalance: 'Add Balance',
            manualBalanceHelp: 'Enter the previous due amount to add to customer\'s account',
            description: 'Description',
            recordPayment: 'Record Payment',
            transferBalance: 'Transfer Balance',
            customerToCustomerTransfer: 'Customer to Customer Transfer',
            sourceCustomer: 'Source Customer',
            destinationCustomer: 'Destination Customer',
            transferAmount: 'Transfer Amount',
            transferDate: 'Transfer Date',
            transferDetails: 'Transfer Details',
            transferSummary: 'Transfer Summary',
            transferSuccess: 'Balance transferred successfully',
            transferFailed: 'Transfer failed',
            selectDestinationCustomer: 'Please select destination customer',
            searchDestinationCustomer: 'Search destination customer by name or phone...',
            availableBalance: 'Available Balance',
            sourceBalanceAfter: 'Source Balance After',
            destinationBalanceAfter: 'Destination Balance After',
            noTransfers: 'No transfer history found',
            insufficientBalance: 'Amount exceeds available balance',
            validAmountRequired: 'Please enter a valid amount',
            transferDateRequired: 'Transfer date is required',
            processing: 'Processing...',
            cash: 'Cash',
            cheque: 'Cheque',
            bankTransfer: 'Bank Transfer',
            digitalWallet: 'Digital Wallet',
            receiptNo: 'Receipt No',
            date: 'Date',
            amount: 'Amount',
            paymentMethod: 'Payment Method',
            paymentDate: 'Payment Date',
            referenceNumber: 'Reference Number',
            notes: 'Notes',
            cancel: 'Cancel',
            savePayment: 'Save Payment',
            paymentSuccess: 'Payment recorded successfully',
            error: 'Error',
            success: 'Success',
            loading: 'Loading...',
            saving: 'Saving...',
            noTransactions: 'No transactions found',
            noPayments: 'No payments found',
            searchCustomer: 'Search customers...',
            selectCustomer: 'Select a Customer',
            selectFromList: 'Select a customer from the list to view their account details',
            customerInfo: 'Customer',
            phone: 'Phone',
            email: 'Email',
            fromDate: 'From',
            toDate: 'To',
            filter: 'Filter',
            apply: 'Apply',
            clear: 'Clear',
            customers: 'Customers',
            noCustomers: 'No customers found',
            thankYou: 'Thank you for your payment!',
            close: 'Close',
            print: 'Print',
            balanceAfter: 'Balance After Payment',
            advancedFilters: 'Advanced Filters',
            clearFilters: 'Clear Filters',
            applyFilters: 'Apply Filters',
            amountRange: 'Amount Range',
            minAmount: 'Min Amount',
            maxAmount: 'Max Amount',
            search: 'Search',
            searchByDescription: 'Search by description, reference, notes...',
            today: 'Today',
            yesterday: 'Yesterday',
            thisWeek: 'This Week',
            thisMonth: 'This Month',
            last3Months: 'Last 3 Months',
            thisYear: 'This Year',
            all: 'All',
            transactionType: 'Transaction Type',
            dateRange: 'Date Range'
        };
        setTranslations(defaultTexts);

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';

        const interval = setInterval(() => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) setCurrentLanguage(newLang);
        }, 500);
        return () => clearInterval(interval);
    }, [currentLanguage]);

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };

    const loadAllCustomers = useCallback(async () => {
        if (!user?.username || !currentShop) return;
        setLoadingCustomers(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_all_customers_account',
                    shop_id: currentShop.shop_id,
                    username: user.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') setAllCustomers(data.data || []);
            else setAllCustomers([]);
        } catch (error) {
            console.error('Error loading customers:', error);
            setAllCustomers([]);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user?.username, currentShop]);

    const loadCustomerAccount = async (customerId) => {
        if (!currentShop) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_customer_account',
                    customer_id: customerId,
                    shop_id: currentShop.shop_id,
                    username: user?.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setAccountData(data.data);
                setSelectedCustomer(prev => prev ? { ...prev, current_balance: data.data.current_balance } : prev);
                setFilteredTransactions(data.data.transactions || []);
                setFilteredPayments(data.data.payments || []);
                setFilteredTransfers(data.data.transfers || []);
                setFilteredManualBalances(data.data.manual_balances || []);
                resetFilters();
            } else {
                showToast('error', data.message || 'Failed to load account');
            }
        } catch (error) {
            console.error('Error loading account:', error);
            showToast('error', 'Network error');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = useCallback((filterValues) => {
        let transactions = accountData.transactions || [];
        let payments = accountData.payments || [];
        let transfers = accountData.transfers || [];
        let manualBalances = accountData.manual_balances || [];

        if (filterValues.from_date) {
            const fromDate = new Date(filterValues.from_date);
            transactions = transactions.filter(t => new Date(t.transaction_date) >= fromDate);
            payments = payments.filter(p => new Date(p.transaction_date) >= fromDate);
            transfers = transfers.filter(t => new Date(t.transfer_date) >= fromDate);
            manualBalances = manualBalances.filter(m => new Date(m.transaction_date) >= fromDate);
        }
        if (filterValues.to_date) {
            const toDate = new Date(filterValues.to_date);
            toDate.setHours(23, 59, 59, 999);
            transactions = transactions.filter(t => new Date(t.transaction_date) <= toDate);
            payments = payments.filter(p => new Date(p.transaction_date) <= toDate);
            transfers = transfers.filter(t => new Date(t.transfer_date) <= toDate);
            manualBalances = manualBalances.filter(m => new Date(m.transaction_date) <= toDate);
        }

        if (filterValues.transaction_type) {
            transactions = transactions.filter(t => t.transaction_type === filterValues.transaction_type);
        }

        if (filterValues.payment_method) {
            payments = payments.filter(p => p.payment_method === filterValues.payment_method);
            transactions = transactions.filter(t =>
                t.transaction_type !== 'payment' || t.payment_method === filterValues.payment_method
            );
        }

        if (filterValues.min_amount) {
            const minAmount = parseFloat(filterValues.min_amount);
            transactions = transactions.filter(t => Math.abs(t.amount) >= minAmount);
            payments = payments.filter(p => p.amount >= minAmount);
            transfers = transfers.filter(t => t.amount >= minAmount);
            manualBalances = manualBalances.filter(m => m.amount >= minAmount);
        }
        if (filterValues.max_amount) {
            const maxAmount = parseFloat(filterValues.max_amount);
            transactions = transactions.filter(t => Math.abs(t.amount) <= maxAmount);
            payments = payments.filter(p => p.amount <= maxAmount);
            transfers = transfers.filter(t => t.amount <= maxAmount);
            manualBalances = manualBalances.filter(m => m.amount <= maxAmount);
        }

        if (filterValues.search_text) {
            const searchLower = filterValues.search_text.toLowerCase();
            transactions = transactions.filter(t =>
                (t.description && t.description.toLowerCase().includes(searchLower)) ||
                (t.reference_number && t.reference_number.toLowerCase().includes(searchLower)) ||
                (t.notes && t.notes.toLowerCase().includes(searchLower))
            );
            payments = payments.filter(p =>
                (p.description && p.description.toLowerCase().includes(searchLower)) ||
                (p.reference_number && p.reference_number.toLowerCase().includes(searchLower)) ||
                (p.notes && p.notes.toLowerCase().includes(searchLower))
            );
            transfers = transfers.filter(t =>
                (t.transfer_no && t.transfer_no.toLowerCase().includes(searchLower)) ||
                (t.from_customer_name && t.from_customer_name.toLowerCase().includes(searchLower)) ||
                (t.to_customer_name && t.to_customer_name.toLowerCase().includes(searchLower)) ||
                (t.reference_no && t.reference_no.toLowerCase().includes(searchLower)) ||
                (t.notes && t.notes.toLowerCase().includes(searchLower))
            );
            manualBalances = manualBalances.filter(m =>
                (m.description && m.description.toLowerCase().includes(searchLower)) ||
                (m.reference_number && m.reference_number.toLowerCase().includes(searchLower)) ||
                (m.notes && m.notes.toLowerCase().includes(searchLower))
            );
        }

        setFilteredTransactions(transactions);
        setFilteredPayments(payments);
        setFilteredTransfers(transfers);
        setFilteredManualBalances(manualBalances);
    }, [accountData.transactions, accountData.payments, accountData.transfers, accountData.manual_balances]);

    const resetFilters = () => {
        const defaultFilters = {
            from_date: '',
            to_date: '',
            transaction_type: '',
            payment_method: '',
            min_amount: '',
            max_amount: '',
            search_text: ''
        };
        setFilters(defaultFilters);
        setFilteredTransactions(accountData.transactions || []);
        setFilteredPayments(accountData.payments || []);
        setFilteredTransfers(accountData.transfers || []);
        setFilteredManualBalances(accountData.manual_balances || []);
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = (data) => {
        if (!data || !data.length) return [];

        const sorted = [...data];
        sorted.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'transaction_date' || sortConfig.key === 'sale_date' || sortConfig.key === 'transfer_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            else if (sortConfig.key === 'amount') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }
            else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    const searchSidebarCustomers = useCallback(async (term) => {
        if (!user?.username || !currentShop) return;
        if (!term || term.length < 2) { loadAllCustomers(); return; }
        setLoadingCustomers(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_customers_account',
                    search_term: term,
                    shop_id: currentShop.shop_id,
                    username: user.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') setAllCustomers(data.data || []);
        } catch (error) {
            console.error('Error searching:', error);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user?.username, currentShop, loadAllCustomers]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (searchTerm.length >= 2) searchSidebarCustomers(searchTerm);
            else if (searchTerm.length === 0) loadAllCustomers();
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm, searchSidebarCustomers, loadAllCustomers]);

    const selectCustomer = (customer) => {
        setSelectedCustomer(customer);
        loadCustomerAccount(customer.customer_id);
    };

    const handlePaymentSubmit = async (paymentData) => {
        if (!selectedCustomer || !currentShop) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'record_payment',
                    customer_id: selectedCustomer.customer_id,
                    shop_id: currentShop.shop_id,
                    username: user?.username,
                    ...paymentData
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', data.message || translations.paymentSuccess);
                await loadCustomerAccount(selectedCustomer.customer_id);
                await loadAllCustomers();
                setSelectedPayment({
                    ...data.data,
                    payment_method: paymentData.payment_method
                });
                setShowReceiptModal(true);
            } else {
                showToast('error', data.message || 'Failed to record payment');
            }
        } catch (error) {
            console.error('Payment error:', error);
            showToast('error', 'Network error');
        }
    };

    const handleTransferSubmit = async (transferData) => {
        if (!selectedCustomer || !currentShop) return;
        await loadCustomerAccount(selectedCustomer.customer_id);
        await loadAllCustomers();
    };

    const handleManualBalanceSubmit = async (balanceData) => {
        if (!selectedCustomer || !currentShop) return;
        await loadCustomerAccount(selectedCustomer.customer_id);
        await loadAllCustomers();
    };

    const refreshAfterTransfer = async () => {
        if (selectedCustomer) {
            await loadCustomerAccount(selectedCustomer.customer_id);
            await loadAllCustomers();
        }
    };

    const refreshAfterManualBalance = async () => {
        if (selectedCustomer) {
            await loadCustomerAccount(selectedCustomer.customer_id);
            await loadAllCustomers();
        }
    };

    const formatCurrency = (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`;
    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('en-IN') : '';

    const filteredCustomers = allCustomers.filter(customer => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (customer.customer_name?.toLowerCase().includes(searchLower)) ||
            (customer.phone?.includes(searchTerm));
    });

    useEffect(() => {
        if (currentShop && user?.username) loadAllCustomers();
    }, [currentShop, user?.username, loadAllCustomers]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    const sortedTransactions = getSortedData(filteredTransactions);
    const sortedPayments = getSortedData(filteredPayments);
    const sortedTransfers = getSortedData(filteredTransfers);
    const sortedManualBalances = getSortedData(filteredManualBalances);

    const transactionTypes = ['sale', 'payment', 'adjustment', 'refund'];
    const paymentMethods = ['cash', 'cheque', 'bank_transfer', 'digital_wallet', 'card', 'upi'];

    // Apply theme colors dynamically
    useEffect(() => {
        const themeColor = localStorage.getItem('ourzest_theme') || '#6366f1';
        const root = document.documentElement;
        const hex = themeColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        const adjust = (amount) => {
            let newR = Math.max(0, Math.min(255, r + amount));
            let newG = Math.max(0, Math.min(255, g + amount));
            let newB = Math.max(0, Math.min(255, b + amount));
            return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        };

        root.style.setProperty('--ca-primary', themeColor);
        root.style.setProperty('--ca-primary-dark', adjust(-40));
        root.style.setProperty('--ca-primary-light', adjust(40));
        root.style.setProperty('--ca-primary-ultralight', adjust(20));
        root.style.setProperty('--ca-primary-gradient', `linear-gradient(135deg, ${themeColor}, ${adjust(-20)})`);
        root.style.setProperty('--ca-primary-shadow', `${themeColor}20`);
    }, []);

    return (
        <div className={`ca-container ${isRTL ? 'ca-rtl' : ''}`}>
            <CaToastNotification toast={toast} setToast={setToast} translations={translations} />

            <CaEnhancedPaymentModal
                show={showSimplePaymentModal}
                customer={selectedCustomer}
                onClose={() => setShowSimplePaymentModal(false)}
                onSave={handlePaymentSubmit}
                translations={translations}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaCustomerTransferModal
                show={showTransferModal}
                fromCustomer={selectedCustomer}
                onClose={() => setShowTransferModal(false)}
                onSave={handleTransferSubmit}
                onRefresh={refreshAfterTransfer}
                translations={translations}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaManualBalanceModal
                show={showManualBalanceModal}
                customer={selectedCustomer}
                onClose={() => setShowManualBalanceModal(false)}
                onSave={handleManualBalanceSubmit}
                onRefresh={refreshAfterManualBalance}
                translations={translations}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaPaymentReceiptModal
                show={showReceiptModal}
                payment={selectedPayment}
                onClose={() => setShowReceiptModal(false)}
                translations={translations}
            />

            <div className="ca-header">
                <div className="ca-header-content">
                    <div className="ca-header-icon"><FontAwesomeIcon icon={faUsers} /></div>
                    <div className="ca-header-text">
                        <h1>{translations.customerAccount}</h1>
                        <p>{translations.accountDetails}</p>
                        {currentShop && (
                            <div className="ca-shop-badge"><FontAwesomeIcon icon={faTag} /> {currentShop.shop_name}</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="ca-main-layout">
                <div className={`ca-sidebar ${sidebarCollapsed ? 'ca-collapsed' : ''}`}>
                    <div className="ca-sidebar-header">
                        <button className="ca-sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="ca-sidebar-title"><FontAwesomeIcon icon={faUsers} /><span>{translations.customers}</span></div>
                        )}
                    </div>

                    {!sidebarCollapsed && (
                        <>
                            <div className="ca-sidebar-search">
                                <div className="ca-search-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="ca-search-icon" />
                                    <input type="text" placeholder={translations.searchCustomer} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                    {loadingCustomers && <FontAwesomeIcon icon={faSpinner} spin className="ca-search-spinner" />}
                                </div>
                            </div>
                            <div className="ca-customer-list">
                                {loadingCustomers ? (
                                    <div className="ca-loading-customers"><FontAwesomeIcon icon={faSpinner} spin /><span>{translations.loading}</span></div>
                                ) : filteredCustomers.length === 0 ? (
                                    <div className="ca-no-customers"><FontAwesomeIcon icon={faUsers} /><span>{translations.noCustomers}</span></div>
                                ) : (
                                    filteredCustomers.map(customer => (
                                        <div key={customer.customer_id} className={`ca-customer-item ${selectedCustomer?.customer_id === customer.customer_id ? 'ca-active' : ''}`} onClick={() => selectCustomer(customer)}>
                                            <div className="ca-customer-content">
                                                <div className="ca-customer-name"><FontAwesomeIcon icon={faUser} className="ca-item-icon" /><span className="ca-customer-name-text">{customer.customer_name}</span></div>
                                                {customer.phone && <div className="ca-customer-phone"><FontAwesomeIcon icon={faPhone} size="xs" /> {customer.phone}</div>}
                                                <div className={`ca-customer-balance ${parseFloat(customer.current_balance) > 0 ? 'ca-positive' : ''}`}>{formatCurrency(customer.current_balance || 0)}</div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="ca-main-content">
                    {selectedCustomer ? (
                        <div className="ca-account-content">
                            <div className="ca-summary-cards">
                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faUser} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.customerInfo}</div>
                                        <div className="ca-card-value">{selectedCustomer.customer_name}</div>
                                        {selectedCustomer.phone && <div className="ca-card-subvalue"><FontAwesomeIcon icon={faPhone} size="xs" /> {selectedCustomer.phone}</div>}
                                    </div>
                                </div>
                                <div className={`ca-summary-card ca-balance-card ${parseFloat(accountData.current_balance) > 0 ? 'ca-balance-positive' : ''}`}>
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.currentBalance}</div>
                                        <div className="ca-card-value">{formatCurrency(accountData.current_balance)}</div>
                                    </div>
                                </div>
                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faMoneyBill} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.totalPayments}</div>
                                        <div className="ca-card-value">{formatCurrency(accountData.account?.total_payments || 0)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-action-buttons">
                                <button className="ca-btn-secondary" onClick={() => setShowSimplePaymentModal(true)}>
                                    <FontAwesomeIcon icon={faMoneyBill} /> {translations.recordPayment || 'Record Payment'}
                                </button>
                                <button className="ca-btn-transfer" onClick={() => setShowTransferModal(true)}>
                                    <FontAwesomeIcon icon={faExchangeAlt} /> {translations.transferBalance || 'Transfer Balance'}
                                </button>
                                <button className="ca-btn-manual" onClick={() => setShowManualBalanceModal(true)}>
                                    <FontAwesomeIcon icon={faPlusCircle} /> {translations.manualBalance || 'Add Manual Balance'}
                                </button>
                                <button
                                    className={`ca-btn-filter ${showFilters ? 'ca-active' : ''}`}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <FontAwesomeIcon icon={faFilter} /> {translations.filter || 'Filter'}
                                    {Object.values(filters).some(v => v) && <span className="ca-filter-badge"></span>}
                                </button>
                            </div>

                            <CaAdvancedFilters
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                onClearFilters={resetFilters}
                                onApplyFilters={applyFilters}
                                showFilters={showFilters}
                                setShowFilters={setShowFilters}
                                translations={translations}
                                transactionTypes={transactionTypes}
                                paymentMethods={paymentMethods}
                            />

                            <div className="ca-tabs">
                                <button className={`ca-tab-btn ${activeTab === 'transactions' ? 'ca-active' : ''}`} onClick={() => setActiveTab('transactions')}>
                                    <FontAwesomeIcon icon={faHistory} /> {translations.transactionHistory}
                                    {filteredTransactions.length !== accountData.transactions?.length && filteredTransactions.length > 0 && (
                                        <span className="ca-tab-count">{filteredTransactions.length}</span>
                                    )}
                                </button>
                                <button className={`ca-tab-btn ${activeTab === 'payments' ? 'ca-active' : ''}`} onClick={() => setActiveTab('payments')}>
                                    <FontAwesomeIcon icon={faMoneyBill} /> {translations.payments}
                                    {filteredPayments.length !== accountData.payments?.length && filteredPayments.length > 0 && (
                                        <span className="ca-tab-count">{filteredPayments.length}</span>
                                    )}
                                </button>
                                <button className={`ca-tab-btn ${activeTab === 'transfers' ? 'ca-active' : ''}`} onClick={() => setActiveTab('transfers')}>
                                    <FontAwesomeIcon icon={faExchangeAlt} /> {translations.transfers || 'Transfers'}
                                    {filteredTransfers.length !== accountData.transfers?.length && filteredTransfers.length > 0 && (
                                        <span className="ca-tab-count">{filteredTransfers.length}</span>
                                    )}
                                </button>
                                <button className={`ca-tab-btn ${activeTab === 'manual_balances' ? 'ca-active' : ''}`} onClick={() => setActiveTab('manual_balances')}>
                                    <FontAwesomeIcon icon={faPlusCircle} /> Manual Balances
                                    {filteredManualBalances.length !== accountData.manual_balances?.length && filteredManualBalances.length > 0 && (
                                        <span className="ca-tab-count">{filteredManualBalances.length}</span>
                                    )}
                                </button>
                            </div>

                            <div className="ca-tab-content">
    {activeTab === 'transactions' && (
        <div className="ca-table-container">
            <table className="ca-table">
                <thead>
                    <tr>
                        <CaSortableHeader column="transaction_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="transaction_type" label="Type" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="description" label={translations.description || 'Description'} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="amount" label="Amount" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="balance_after" label={translations.balanceAfter || 'Balance After'} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                    </tr>
                </thead>
                <tbody>
                    {sortedTransactions.map((t, i) => (
                        <tr key={t.id || i}>
                            <td>{formatDate(t.transaction_date)}</td>
                            <td>
                                <span className={`ca-transaction-type ca-${t.transaction_type}`}>
                                    {t.transaction_type === 'sale' ? 'Sale' :
                                     t.transaction_type === 'payment' ? 'Payment' :
                                     t.transaction_type === 'adjustment' ? 'Adjustment' : 'Refund'}
                                </span>
                            </td>
                            <td>{t.description || '-'}</td>
                            <td className={`ca-text-right ${t.transaction_type === 'sale' ? 'ca-debit' : 'ca-credit'}`}>
                                {t.transaction_type === 'sale' 
                                    ? `+ ${formatCurrency(t.amount)}`
                                    : `- ${formatCurrency(Math.abs(t.amount))}`}
                            </td>
                            <td className="ca-text-right">{formatCurrency(t.balance_after)}</td>
                        </tr>
                    ))}
                    {sortedTransactions.length === 0 && (
                        <tr>
                            <td colSpan="5" className="ca-text-center ca-no-data">
                                {filters.search_text || filters.from_date || filters.to_date || filters.transaction_type || filters.min_amount || filters.max_amount
                                    ? 'No transactions match your filters'
                                    : translations.noTransactions}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )}

    {activeTab === 'payments' && (
        <div className="ca-table-container">
            <table className="ca-table">
                <thead>
                    <tr>
                        <CaSortableHeader column="reference_number" label={translations.receiptNo} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="transaction_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="payment_method" label={translations.paymentMethod} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="amount" label={translations.amount} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <th>{translations.notes}</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPayments.map(payment => (
                        <tr key={payment.id}>
                            <td>{payment.reference_number}</td>
                            <td>{formatDate(payment.transaction_date)}</td>
                            <td><span className="ca-payment-method">{payment.payment_method}</span></td>
                            <td className="ca-text-right ca-positive">{formatCurrency(payment.amount)}</td>
                            <td>{payment.notes || '-'}</td>
                            <td>
                                <button className="ca-btn-icon" onClick={() => {
                                    setSelectedPayment({
                                        receipt_no: payment.reference_number,
                                        payment_date: payment.transaction_date,
                                        payment_method: payment.payment_method,
                                        amount: payment.amount,
                                        balance_after: payment.balance_after,
                                        customer_name: selectedCustomer.customer_name,
                                        reference_no: payment.notes?.match(/Ref: (.*)/)?.[1]
                                    });
                                    setShowReceiptModal(true);
                                }}>
                                    <FontAwesomeIcon icon={faPrint} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {sortedPayments.length === 0 && (
                        <tr>
                            <td colSpan="6" className="ca-text-center ca-no-data">
                                {filters.search_text || filters.from_date || filters.to_date || filters.payment_method || filters.min_amount || filters.max_amount
                                    ? 'No payments match your filters'
                                    : translations.noPayments}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )}

    {activeTab === 'transfers' && (
        <div className="ca-table-container">
            <table className="ca-table">
                <thead>
                    <tr>
                        <CaSortableHeader column="transfer_no" label="Transfer No" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="transfer_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="from_customer_name" label="From" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="to_customer_name" label="To" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="amount" label={translations.amount} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <th>{translations.referenceNumber}</th>
                        <th>{translations.notes}</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedTransfers.map(transfer => (
                        <tr key={transfer.id}>
                            <td>{transfer.transfer_no}</td>
                            <td>{formatDate(transfer.transfer_date)}</td>
                            <td>
                                {transfer.from_customer_id === selectedCustomer?.customer_id ?
                                    <strong>{transfer.from_customer_name}</strong> :
                                    transfer.from_customer_name}
                            </td>
                            <td>
                                {transfer.to_customer_id === selectedCustomer?.customer_id ?
                                    <strong>{transfer.to_customer_name}</strong> :
                                    transfer.to_customer_name}
                            </td>
                            <td className={`ca-text-right ${transfer.from_customer_id === selectedCustomer?.customer_id ? 'ca-negative' : 'ca-positive'}`}>
                                {transfer.from_customer_id === selectedCustomer?.customer_id ? '-' : '+'}
                                {formatCurrency(transfer.amount)}
                            </td>
                            <td>{transfer.reference_no || '-'}</td>
                            <td>{transfer.notes || '-'}</td>
                        </tr>
                    ))}
                    {sortedTransfers.length === 0 && (
                        <tr>
                            <td colSpan="7" className="ca-text-center ca-no-data">
                                {translations.noTransfers || 'No transfer history found'}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )}

    {activeTab === 'manual_balances' && (
        <div className="ca-table-container">
            <table className="ca-table">
                <thead>
                    <tr>
                        <CaSortableHeader column="reference_number" label="Reference No" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="transaction_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="description" label="Description" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <CaSortableHeader column="amount" label={translations.amount} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                        <th>{translations.notes}</th>
                        <th>Added By</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedManualBalances.map(balance => (
                        <tr key={balance.id}>
                            <td>{balance.reference_number}</td>
                            <td>{formatDate(balance.transaction_date)}</td>
                            <td>{balance.description || '-'}</td>
                            <td className="ca-text-right ca-positive">+{formatCurrency(balance.amount)}</td>
                            <td>{balance.notes || '-'}</td>
                            <td>{balance.username || '-'}</td>
                        </tr>
                    ))}
                    {sortedManualBalances.length === 0 && (
                        <tr>
                            <td colSpan="6" className="ca-text-center ca-no-data">
                                {filters.search_text || filters.from_date || filters.to_date || filters.min_amount || filters.max_amount
                                    ? 'No manual balances match your filters'
                                    : 'No manual balance history found'}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )}
</div>

                            {Object.values(filters).some(v => v) && (
                                <div className="ca-filter-summary">
                                    <span className="ca-filter-summary-label">Active Filters:</span>
                                    {filters.from_date && <span className="ca-filter-tag">From: {filters.from_date}</span>}
                                    {filters.to_date && <span className="ca-filter-tag">To: {filters.to_date}</span>}
                                    {filters.transaction_type && <span className="ca-filter-tag">Type: {filters.transaction_type}</span>}
                                    {filters.payment_method && <span className="ca-filter-tag">Method: {filters.payment_method}</span>}
                                    {filters.min_amount && <span className="ca-filter-tag">Min: {formatCurrency(filters.min_amount)}</span>}
                                    {filters.max_amount && <span className="ca-filter-tag">Max: {formatCurrency(filters.max_amount)}</span>}
                                    {filters.search_text && <span className="ca-filter-tag">Search: "{filters.search_text}"</span>}
                                    <button className="ca-clear-filters" onClick={resetFilters}>
                                        <FontAwesomeIcon icon={faTimes} /> Clear All
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="ca-no-selected">
                            <FontAwesomeIcon icon={faUsers} className="ca-empty-icon" />
                            <h3>{translations.selectCustomer}</h3>
                            <p>{translations.selectFromList}</p>
                        </div>
                    )}
                </div>
            </div>

            {loading && (
                <div className="ca-loading-overlay">
                    <div className="ca-loading-content">
                        <div className="ca-loading-spinner"></div>
                        <p>{translations.loading}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerAccount;